
import { SelectHTMLAttributes, useId } from 'react';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { components, DropdownIndicatorProps, InputActionMeta } from 'react-select';
import CreatableReactSelect from 'react-select/creatable';
import { OmitStrict } from 'type-zoo';

import { FieldError, FieldProps, Icon, Label, LabelProps, SelectOption } from '@dansk-metal/ui';

import styles from './creatable-select.module.scss';

export type CreatableSelectProps<TKey extends string, T extends SelectOption> = {
	fieldState?: ControllerFieldState;
	options: T[] | Readonly<T[]>;
	isLoading?: boolean;
	onInputChange?: (input: string, actionMeta: InputActionMeta) => void;
	inputValue?: string;
	value?: T;
	dropdownIndicator?: (props: DropdownIndicatorProps) => JSX.Element;
} & LabelProps &
OmitStrict<SelectHTMLAttributes<HTMLSelectElement>, 'form' | 'value'> &
FieldProps<TKey> &
Partial<OmitStrict<ControllerRenderProps, 'ref'>>;

export function CreatableSelect<TKey extends string, T extends SelectOption>({
	description,
	fieldState,
	id,
	label,
	labelPosition = 'over',
	required,
	options,
	noOptionalLabel,
	disabled,
	...rest
}: CreatableSelectProps<TKey, T>) {
	return (
		<Label
			label={label}
			id={id}
			required={required}
			labelPosition={labelPosition}
			description={description}
			disabled={disabled}
			noOptionalLabel={noOptionalLabel}
			className={styles.selectLabel}
		>
			<SelectCreatable disabled={disabled} options={options} label={label} {...rest}/>
			<FieldError error={fieldState?.error} />
		</Label>
	);
}

const DropDownIndicatorElem = (props: DropdownIndicatorProps) => {
	return (
		<components.DropdownIndicator {...props}>
			<Icon icon="chevron-down" size="24px" />
		</components.DropdownIndicator>
	);
};

function SelectCreatable<TKey extends string, T extends SelectOption>({
	options,
	value,
	onChange,
	onBlur,
	disabled,
	placeholder,
	isLoading,
	onInputChange,
	inputValue,
	dropdownIndicator = DropDownIndicatorElem,
	label,
}: CreatableSelectProps<TKey, T>) {
	return (
		<CreatableReactSelect
			classNamePrefix='react-select'
			classNames={{ container: () => styles.container }}
			unstyled={true}
			placeholder={placeholder}
			isDisabled={disabled}
			options={options}
			value={value}
			isLoading={isLoading}
			inputValue={inputValue}
			onInputChange={onInputChange}
			onChange={onChange}
			onBlur={onBlur}
			instanceId={useId()}
			noOptionsMessage={() => null}
			loadingMessage={() => null}
			components={{ DropdownIndicator: dropdownIndicator }}
			createOptionPosition='first'
			formatCreateLabel={(input: string) => (`Opret ny ${String(label).toLowerCase()} “${input}”`)}
			isValidNewOption={(input: string) => input.length > 2}
		/>
	);
}
